/**
 * @voltxampere/web — Font size preference (Sprint 80.Z L-AYARLAR-1).
 *
 * Closes the AYARLAR audit Phase A finding: 4 fontSize buttons
 * (S / M / L / XL) under Erişilebilirlik sub-tab were placeholder
 * (UI present, no signal, no click handler, no persist, no CSS).
 *
 * This signal mirrors the B1 (Sprint 80.Z, high-contrast) canonical
 * pattern: Signal + IDB persist via A11ySettings.fontSize +
 * boot-time hydrate + main.ts effect that toggles a body class.
 *
 * The CSS effect (in global.css) uses `body.font-{S,L,XL}` class
 * overrides on a curated surface set (topbar tabs, settings panes,
 * inspector measurement values) rather than `<html>` rem cascade —
 * the canvas pane has tight grid math that breaks under rem
 * inflation. M is the default with no class applied.
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */

import { signal } from "@preact/signals-core";
import { getSettings, mergeSettings, type FontSize } from "@voltxampere/persistence";

export type { FontSize };

export const FONT_SIZES: readonly FontSize[] = ["S", "M", "L", "XL"] as const;

export const fontSize = signal<FontSize>("M");

let initialised = false;

export function setFontSize(size: FontSize): void {
  if (fontSize.value === size) return;
  fontSize.value = size;
  void persist();
}

async function persist(): Promise<void> {
  try {
    const existing = await getSettings();
    await mergeSettings({
      a11y: {
        highContrast: existing?.a11y?.highContrast === true,
        reducedMotion: existing?.a11y?.reducedMotion === true,
        fontSize: fontSize.value,
      },
    });
  } catch {
    // ignore — soft-fail (private mode quota etc.)
  }
}

export async function initFontSizePrefs(): Promise<void> {
  if (initialised) return;
  initialised = true;
  try {
    const settings = await getSettings();
    const persisted = settings?.a11y?.fontSize;
    if (persisted && FONT_SIZES.includes(persisted)) {
      fontSize.value = persisted;
    }
  } catch {
    // ignore
  }
}
